import { useState, useEffect, useRef } from 'react';
import BottomSheet from './BottomSheet';

export default function BottomSheetSelect({
  value,
  onChange,
  options = [],
  placeholder = '선택하세요',
  title,
  searchable = false,
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchRef = useRef(null);

  const selectedOption = options.find(opt => String(opt.value) === String(value));

  const filteredOptions = searchable && search
    ? options.filter(opt =>
        opt.label.toLowerCase().includes(search.toLowerCase()) ||
        (opt.sublabel && opt.sublabel.toLowerCase().includes(search.toLowerCase()))
      )
    : options;

  useEffect(() => {
    if (isOpen) {
      setSearch('');
    }
  }, [isOpen]);

  const closeSheet = () => {
    setIsOpen(false);
  };

  const handleSelect = (optValue) => {
    onChange({ target: { value: String(optValue) } });
    closeSheet();
  };

  return (
    <>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(true)}
        disabled={disabled}
        className={`w-full px-4 py-3 bg-gray-50 border border-border rounded-xl text-sm text-left flex items-center justify-between transition-all ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-tayo/30 active:scale-[0.99]'
        }`}
      >
        <span className={selectedOption ? 'text-charcoal font-medium' : 'text-sub-text'}>
          {selectedOption
            ? (selectedOption.sublabel ? `${selectedOption.label} (${selectedOption.sublabel})` : selectedOption.label)
            : placeholder
          }
        </span>
        <svg className="w-4 h-4 text-sub-text flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Bottom Sheet */}
      <BottomSheet isOpen={isOpen} onClose={closeSheet} minHeight="30vh" maxHeight="70vh">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pb-3">
          <h3 className="text-lg font-bold text-charcoal">{title || placeholder}</h3>
          <button
            onClick={closeSheet}
            className="p-1.5 text-sub-text hover:text-charcoal hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        {searchable && (
          <div className="px-5 pb-3">
            <div className="relative">
              <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-sub-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="검색..."
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-tayo focus:border-transparent transition-all"
              />
            </div>
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-border" />

        {/* Options */}
        <div className="overflow-y-auto flex-1 overscroll-contain pb-6 pb-[env(safe-area-inset-bottom)]">
          {filteredOptions.length === 0 ? (
            <p className="text-center text-sub-text py-8 text-sm">
              {search ? '검색 결과가 없습니다' : '선택 가능한 항목이 없습니다'}
            </p>
          ) : (
            filteredOptions.map((opt) => {
              const isSelected = String(opt.value) === String(value);
              return (
                <button
                  key={opt.value}
                  onClick={() => handleSelect(opt.value)}
                  className={`w-full px-5 py-3.5 flex items-center justify-between text-left transition-colors ${
                    isSelected
                      ? 'bg-tayo-light'
                      : 'hover:bg-gray-50 active:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${isSelected ? 'text-tayo' : 'text-charcoal'}`}>
                      {opt.label}
                    </span>
                    {opt.sublabel && (
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                        isSelected
                          ? 'bg-tayo/10 text-tayo'
                          : opt.sublabelColor
                            ? `bg-gray-100 ${opt.sublabelColor}`
                            : 'bg-gray-100 text-sub-text'
                      }`}>
                        {opt.sublabel}
                      </span>
                    )}
                  </div>
                  {isSelected && (
                    <svg className="w-5 h-5 text-tayo flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              );
            })
          )}
        </div>
      </BottomSheet>
    </>
  );
}
